import { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import GlassCard from "../components/GlassCard";
import { AuthContext } from "../context/AuthContext";
import { useThemeColors } from "../context/ThemeContext";
import { useToast } from "../components/ToastProvider";
import { useTemplates, useUpdateTemplates } from "../hooks/queries";
import { PLACEHOLDER_HELP } from "../utils/messageTemplate";
import { HEADING_FONT } from "../theme/fonts";

function SectionHeader({ icon, title, subtitle, colors, color = "#818cf8" }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontSize: 16,
            fontWeight: 700,
            color: colors.text,
            lineHeight: 1.2,
            mt: "2px",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12.5, color: colors.textMuted }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function FieldLabel({ children, colors }) {
  return (
    <Typography
      component="label"
      sx={{
        display: "block",
        fontSize: 13,
        fontWeight: 600,
        color: colors.text,
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function Settings() {
  const colors = useThemeColors();
  const toast = useToast();
  const { isAdmin } = useContext(AuthContext);

  // Shared TextField styling so inputs match the rest of the app.
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: 2,
      fontSize: 14,
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#818cf8" },
    },
    "& input, & textarea": { color: colors.text },
    "& input::placeholder, & textarea::placeholder": {
      color: colors.textFaint,
      opacity: 1,
    },
  };

  // --- Templates -------------------------------------------------------------
  const templatesQuery = useTemplates();
  const updateTemplates = useUpdateTemplates();
  const [tpl, setTpl] = useState({
    whatsappTemplate: "",
    emailSubject: "",
    emailTemplate: "",
  });

  useEffect(() => {
    if (templatesQuery.data) setTpl(templatesQuery.data);
  }, [templatesQuery.data]);

  async function saveTemplates() {
    try {
      await updateTemplates.mutateAsync(tpl);
      toast.success("Message templates saved");
    } catch (err) {
      toast.error(err.message || "Failed to save templates");
    }
  }

  const primaryBtnSx = {
    textTransform: "none",
    fontWeight: 600,
    px: 3,
    py: 1,
    borderRadius: 2,
    color: "#fff",
    background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
    "&:hover": { boxShadow: "0 8px 22px rgba(79, 70, 229, 0.45)" },
    "&.Mui-disabled": {
      color: "rgba(255,255,255,0.5)",
      background:
        "linear-gradient(135deg, rgba(79,70,229,0.5) 0%, rgba(124,58,237,0.5) 100%)",
    },
  };

  return (
    <DashboardLayout>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <SettingsOutlinedIcon sx={{ color: "#818cf8", fontSize: 28 }} />
        <Box>
          <Typography
            sx={{
              fontFamily: HEADING_FONT,
              fontSize: 22,
              fontWeight: 800,
              color: colors.text,
            }}
          >
            Settings
          </Typography>
          <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
            Edit the client reminder messages
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={3}>
        {/* Message templates */}
        <GlassCard sx={{ p: 3 }}>
          <SectionHeader
            icon={<ForumOutlinedIcon />}
            title="Client Reminder Messages"
            subtitle="Pre-typed messages sent from a domain's page"
            colors={colors}
            color="#7c3aed"
          />

          {/* Placeholder help */}
          <Box
            sx={{
              p: 1.75,
              mb: 2.5,
              borderRadius: 2,
              background: colors.hover,
              border: `1px solid ${colors.cardBorder}`,
            }}
          >
            <Typography
              sx={{ fontSize: 12, fontWeight: 700, color: colors.text, mb: 1 }}
            >
              Placeholders (auto-filled per domain when sending)
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {PLACEHOLDER_HELP.map((p) => (
                <Chip
                  key={p.token}
                  label={`${p.token} — ${p.label}`}
                  size="small"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 11,
                    background: colors.cardBg,
                    color: colors.textMuted,
                    border: `1px solid ${colors.cardBorder}`,
                  }}
                />
              ))}
            </Stack>
          </Box>

          {!isAdmin && (
            <Typography
              sx={{ fontSize: 12.5, color: colors.textFaint, mb: 2 }}
            >
              Only administrators can edit these templates.
            </Typography>
          )}

          {templatesQuery.isLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={24} sx={{ color: "#818cf8" }} />
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 0.75 }}
                >
                  <WhatsAppIcon sx={{ fontSize: 18, color: "#22c55e" }} />
                  <FieldLabel colors={colors}>WhatsApp message</FieldLabel>
                </Stack>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  disabled={!isAdmin}
                  value={tpl.whatsappTemplate}
                  onChange={(e) =>
                    setTpl((t) => ({ ...t, whatsappTemplate: e.target.value }))
                  }
                  sx={fieldSx}
                />
              </Box>

              <Divider sx={{ borderColor: colors.divider }} />

              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 0.75 }}
                >
                  <EmailOutlinedIcon sx={{ fontSize: 18, color: "#2563eb" }} />
                  <FieldLabel colors={colors}>Email subject</FieldLabel>
                </Stack>
                <TextField
                  fullWidth
                  size="small"
                  disabled={!isAdmin}
                  value={tpl.emailSubject}
                  onChange={(e) =>
                    setTpl((t) => ({ ...t, emailSubject: e.target.value }))
                  }
                  sx={{ ...fieldSx, mb: 1.5 }}
                />
                <FieldLabel colors={colors}>Email message</FieldLabel>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  disabled={!isAdmin}
                  value={tpl.emailTemplate}
                  onChange={(e) =>
                    setTpl((t) => ({ ...t, emailTemplate: e.target.value }))
                  }
                  sx={fieldSx}
                />
              </Box>

              {isAdmin && (
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    onClick={saveTemplates}
                    disabled={updateTemplates.isPending}
                    startIcon={
                      updateTemplates.isPending ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : null
                    }
                    sx={primaryBtnSx}
                  >
                    {updateTemplates.isPending ? "Saving…" : "Save Templates"}
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </GlassCard>
      </Stack>
    </DashboardLayout>
  );
}

export default Settings;
